import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { ApiService } from '../../core/api.service';
import { HandoverLog, HandoverLogDto, HandoverLogPost, HandoverLogPut } from '../../models/handover-log.model';
import { OpsStreamType } from '../../models/filters.model';
import { LogApiService } from './logs.api.service';
import { ProgramsStore } from '../stores/programs.store';
import { UsersStore } from '../stores/users.store';
import { HandoverTaskFormValue } from '../../components/dialogs/task-dialogs/task.dialog.model';
import { zonedWallClockToUtcIso } from '../../shared/utils/datetime';
import { NO_PROGRAM_ID, NO_PROGRAM_LABEL } from '../../shared/constants/program';

@Injectable({ providedIn: 'root' })
export class HandoverLogsService extends LogApiService<HandoverLog, HandoverLogPost, HandoverLogDto> {
  constructor(
    private api: ApiService,
    private programsStore: ProgramsStore,
    private usersStore: UsersStore
  ) {
    super(api, '/handover_logs');
  }

  protected override mapEntity(item: HandoverLogDto): HandoverLog {
    const programName =
      item.program_id === NO_PROGRAM_ID
        ? 'Other'
        : this.programsStore.getNameById(item.program_id) ?? item.program_id ?? 'No Program found';
    const createdByUserName = this.usersStore.getNameById(item.created_by) ?? item.created_by ?? 'No User found'; // fallback if not found

    return {
      id: item.id.toString(),
      programId: item.program_id,
      programName: programName,
      createdByName: createdByUserName,
      createdById: item.created_by,
      dueDate: new Date(item.due_date),
      notes: item.notes,
      status: (item.status as OpsStreamType) ?? OpsStreamType.PENDING,
      createdDate: new Date(item.created_dttm),
      timezone: item.timezone,
      updatedDate: item.updated_dttm,
      updatedBy: item.updated_by,
      activity: item.activity || 'No Activity',
    };
  }

  public override createPayload(formValue: HandoverTaskFormValue, userId: string): HandoverLogPost {
    return {
      activity: formValue.activity,
      program_id: formValue.programId || NO_PROGRAM_LABEL,
      notes: formValue.notes,
      due_date: zonedWallClockToUtcIso(formValue.dueDate, formValue.timezone),
      timezone: formValue.timezone,
      status: OpsStreamType.PENDING,
      created_by: userId,
      created_dttm: new Date().toISOString(),
    };
  }

  buildUpdatedPayload(log: HandoverLog, userId: string): HandoverLogPut {
    return {
      id: Number(log.id),
      program_id: log.programId || NO_PROGRAM_LABEL,
      activity: log.activity,
      notes: log.notes,
      due_date: new Date(log.dueDate).toISOString(),
      timezone: log.timezone,
      created_by: log.createdById,
      created_dttm: new Date(log.createdDate).toISOString(),
      updated_by: userId,
      updated_dttm: new Date().toISOString(),
      status: log.status,
    };
  }

  buildResolvedPayloads(allLogs: HandoverLog[], selectedIds: Set<string>, userId: string): HandoverLogPut[] {
    return allLogs
      .filter((log) => selectedIds.has(log.id))
      .map((log) => ({
        id: Number(log.id),
        program_id: log.programId || NO_PROGRAM_LABEL,
        activity: log.activity,
        notes: log.notes,
        due_date: new Date(log.dueDate).toISOString(),
        timezone: log.timezone,
        created_by: log.createdById,
        created_dttm: new Date(log.createdDate).toISOString(),
        updated_by: userId,
        updated_dttm: new Date().toISOString(),
        status: OpsStreamType.RESOLVED,
      }));
  }

  resolveLogs(payloads: HandoverLogPut[]): Observable<any> {
    const observables = payloads.map((payload) => this.update(payload.id.toString(), payload));
    return forkJoin(observables);
  }
}
